import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { PermissionGate, useRBAC } from '@/contexts/RBACContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building, Home, Plus, MapPin, DollarSign, Bed, Bath, Square,
  Calendar, Users, CheckCircle, Clock, XCircle, Loader2, Eye,
  Sparkles, Edit, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
  { value: 'apartment', label: 'Apartment', icon: '🏢' },
  { value: 'house', label: 'House', icon: '🏠' },
  { value: 'villa', label: 'Villa', icon: '🏡' },
  { value: 'commercial', label: 'Commercial', icon: '🏪' },
  { value: 'office', label: 'Office', icon: '🏛️' },
  { value: 'land', label: 'Land', icon: '🌍' },
  { value: 'warehouse', label: 'Warehouse', icon: '🏭' },
];

const listingTypes = [
  { value: 'sale', label: 'For Sale' },
  { value: 'rent', label: 'For Rent' },
  { value: 'lease', label: 'For Lease' },
  { value: 'short_term', label: 'Short Term' },
];

const bookingStatuses = {
  inquiry: { label: 'Inquiry', color: 'bg-blue-500' },
  pending: { label: 'Pending', color: 'bg-yellow-500' },
  confirmed: { label: 'Confirmed', color: 'bg-green-500' },
  deposit_paid: { label: 'Deposit Paid', color: 'bg-purple-500' },
  completed: { label: 'Completed', color: 'bg-emerald-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500' },
};

const RealEstate = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  const [newProperty, setNewProperty] = useState({
    name: '',
    property_type: 'apartment',
    listing_type: 'sale',
    price: '',
    bedrooms: '',
    bathrooms: '',
    area_sqft: '',
    address: { street: '', city: '', state: '', zip: '', country: '' },
    amenities: [],
    description: ''
  });

  const [newBooking, setNewBooking] = useState({
    property_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    booking_type: 'viewing',
    scheduled_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  // Progressive — properties / bookings / stats each paint as they arrive.
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/real-estate/properties?token=${token}`)
        .then((r) => setProperties(r.data.properties || []))
        .catch((e) => console.error('[RealEstate] properties failed:', e)),
      axios.get(`${API}/real-estate/bookings?token=${token}`)
        .then((r) => setBookings(r.data.bookings || []))
        .catch((e) => console.error('[RealEstate] bookings failed:', e)),
      axios.get(`${API}/real-estate/stats?token=${token}`)
        .then((r) => setStats(r.data.stats || null))
        .catch((e) => console.error('[RealEstate] stats failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const handleCreateProperty = async () => {
    if (!newProperty.name || !newProperty.price) {
      toast.error('Property name and price are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/real-estate/properties?token=${token}`, {
        ...newProperty,
        price: parseFloat(newProperty.price) || 0,
        bedrooms: parseInt(newProperty.bedrooms) || 0,
        bathrooms: parseInt(newProperty.bathrooms) || 0,
        area_sqft: parseFloat(newProperty.area_sqft) || 0
      });

      if (response.data.success) {
        toast.success('Property created!');
        setShowAddProperty(false);
        setNewProperty({
          name: '', property_type: 'apartment', listing_type: 'sale',
          price: '', bedrooms: '', bathrooms: '', area_sqft: '',
          address: { street: '', city: '', state: '', zip: '', country: '' },
          amenities: [], description: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async () => {
    if (!newBooking.property_id || !newBooking.customer_name) {
      toast.error('Property and customer name are required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/real-estate/bookings?token=${token}`, newBooking);

      if (response.data.success) {
        toast.success('Booking created!');
        setShowAddBooking(false);
        setNewBooking({
          property_id: '', customer_name: '', customer_email: '',
          customer_phone: '', booking_type: 'viewing', scheduled_date: '', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/real-estate/bookings/${bookingId}/status?token=${token}`, { status });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleGenerateDescription = async () => {
    if (!newProperty.property_type) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/real-estate/generate-description?token=${token}`, newProperty);
      if (response.data.description) {
        setNewProperty({ ...newProperty, description: response.data.description });
        toast.success('AI description generated!');
      }
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = bookingStatuses[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="real-estate-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-heading font-bold text-primary flex items-center gap-2">
            <Building className="text-accent" />
            Real Estate Management
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Manage properties, units, and bookings
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddBooking(true)} variant="outline">
            <Calendar className="mr-2" size={16} />
            New Booking
          </Button>
          <PermissionGate permission="REAL_ESTATE.PROPERTIES" action="create">
            <Button onClick={() => setShowAddProperty(true)} className="bg-accent">
              <Plus className="mr-2" size={16} />
              Add Property
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Properties</p>
                  <p className="text-2xl font-bold">{stats.properties?.total || 0}</p>
                </div>
                <Building className="text-accent" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Available</p>
                  <p className="text-2xl font-bold text-green-600">{stats.properties?.available || 0}</p>
                </div>
                <CheckCircle className="text-green-500" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Bookings</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.bookings?.pending || 0}</p>
                </div>
                <Clock className="text-yellow-500" size={32} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Portfolio Value</p>
                  <p className="text-2xl font-bold text-accent">
                    AED {(stats.total_portfolio_value || 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="text-accent" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="properties" className="w-full">
        <TabsList>
          <TabsTrigger value="properties">Properties ({properties.length})</TabsTrigger>
          <TabsTrigger value="bookings">Bookings ({bookings.length})</TabsTrigger>
        </TabsList>

        {/* Properties Tab */}
        <TabsContent value="properties" className="mt-6">
          {properties.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No properties yet. Add your first property to get started.</p>
                <Button onClick={() => setShowAddProperty(true)} className="mt-4">
                  <Plus className="mr-2" size={16} />
                  Add Property
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((property) => (
                <Card key={property.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-lg">{property.name}</h4>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin size={12} />
                          {property.address?.city || 'Location TBD'}
                        </p>
                      </div>
                      <Badge variant={property.status === 'available' ? 'default' : 'secondary'}>
                        {property.status}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                      {property.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bed size={14} /> {property.bedrooms}
                        </span>
                      )}
                      {property.bathrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bath size={14} /> {property.bathrooms}
                        </span>
                      )}
                      {property.area_sqft > 0 && (
                        <span className="flex items-center gap-1">
                          <Square size={14} /> {property.area_sqft} sqft
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div>
                        <p className="text-xs text-slate-500">{property.listing_type}</p>
                        <p className="text-lg font-bold text-accent">
                          AED {property.price?.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setSelectedProperty(property)}>
                          <Eye size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setNewBooking({ ...newBooking, property_id: property.id });
                            setShowAddBooking(true);
                          }}
                        >
                          <Calendar size={14} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No bookings yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <Card key={booking.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                          <Users className="text-accent" size={24} />
                        </div>
                        <div>
                          <h4 className="font-semibold">{booking.customer_name}</h4>
                          <p className="text-sm text-slate-500">
                            {booking.property_name} • {booking.booking_type}
                          </p>
                          <p className="text-xs text-slate-400">
                            {booking.scheduled_date || booking.check_in || 'Date TBD'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(booking.status)}
                        {booking.status === 'pending' && (
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                            >
                              <CheckCircle size={14} className="text-green-500" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                            >
                              <XCircle size={14} className="text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Property Modal */}
      {showAddProperty && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Add New Property</CardTitle>
              <CardDescription>Enter property details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Property Name *</Label>
                  <Input
                    value={newProperty.name}
                    onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })}
                    placeholder="Sunset Apartments Unit 4B"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    value={newProperty.price}
                    onChange={(e) => setNewProperty({ ...newProperty, price: e.target.value })}
                    placeholder="250000"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Property Type</Label>
                  <select
                    value={newProperty.property_type}
                    onChange={(e) => setNewProperty({ ...newProperty, property_type: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    {propertyTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Listing Type</Label>
                  <select
                    value={newProperty.listing_type}
                    onChange={(e) => setNewProperty({ ...newProperty, listing_type: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-lg"
                  >
                    {listingTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Bedrooms</Label>
                  <Input
                    type="number"
                    value={newProperty.bedrooms}
                    onChange={(e) => setNewProperty({ ...newProperty, bedrooms: e.target.value })}
                    placeholder="3"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Bathrooms</Label>
                  <Input
                    type="number"
                    value={newProperty.bathrooms}
                    onChange={(e) => setNewProperty({ ...newProperty, bathrooms: e.target.value })}
                    placeholder="2"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Area (sqft)</Label>
                  <Input
                    type="number"
                    value={newProperty.area_sqft}
                    onChange={(e) => setNewProperty({ ...newProperty, area_sqft: e.target.value })}
                    placeholder="1500"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <Input
                    value={newProperty.address.street}
                    onChange={(e) => setNewProperty({ 
                      ...newProperty, 
                      address: { ...newProperty.address, street: e.target.value }
                    })}
                    placeholder="Street Address"
                  />
                  <Input
                    value={newProperty.address.city}
                    onChange={(e) => setNewProperty({ 
                      ...newProperty, 
                      address: { ...newProperty.address, city: e.target.value }
                    })}
                    placeholder="City"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Description</Label>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleGenerateDescription}
                    disabled={loading}
                  >
                    <Sparkles size={14} className="mr-1" />
                    AI Generate
                  </Button>
                </div>
                <textarea
                  value={newProperty.description}
                  onChange={(e) => setNewProperty({ ...newProperty, description: e.target.value })}
                  placeholder="Property description..."
                  className="w-full p-2 border rounded-lg min-h-[100px]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddProperty(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateProperty} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Create Property
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Booking Modal */}
      {showAddBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Create Booking</CardTitle>
              <CardDescription>Schedule a viewing or reservation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Property *</Label>
                <select
                  value={newBooking.property_id}
                  onChange={(e) => setNewBooking({ ...newBooking, property_id: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="">Select Property</option>
                  {properties.map((prop) => (
                    <option key={prop.id} value={prop.id}>{prop.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Booking Type</Label>
                <select
                  value={newBooking.booking_type}
                  onChange={(e) => setNewBooking({ ...newBooking, booking_type: e.target.value })}
                  className="w-full mt-1 p-2 border rounded-lg"
                >
                  <option value="viewing">Property Viewing</option>
                  <option value="reservation">Reservation</option>
                  <option value="purchase">Purchase Intent</option>
                  <option value="rental">Rental Application</option>
                </select>
              </div>

              <div>
                <Label>Customer Name *</Label>
                <Input
                  value={newBooking.customer_name}
                  onChange={(e) => setNewBooking({ ...newBooking, customer_name: e.target.value })}
                  placeholder="John Doe"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newBooking.customer_email}
                    onChange={(e) => setNewBooking({ ...newBooking, customer_email: e.target.value })}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newBooking.customer_phone}
                    onChange={(e) => setNewBooking({ ...newBooking, customer_phone: e.target.value })}
                    placeholder="+1234567890"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Scheduled Date</Label>
                <Input
                  type="datetime-local"
                  value={newBooking.scheduled_date}
                  onChange={(e) => setNewBooking({ ...newBooking, scheduled_date: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <textarea
                  value={newBooking.notes}
                  onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="w-full mt-1 p-2 border rounded-lg min-h-[60px]"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddBooking(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateBooking} disabled={loading}>
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Create Booking
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RealEstate;
